import { useCallback, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';
import './RewardsSearchV2.scss';
import { searchDeals } from '../../utils/service';
import { getGenericError } from '../../utils/errors';
import RewardsSearchResultList from '../RewardsSearchResultList';
import SearchInputButton from '../SearchInputButton/SearchInputButton';
import useScrollToTop from '../../utils/hooks/useScrollToTop';
import { isProd } from '../../../../config/config';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';

const WORD_SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_TIME = 700;
const INITIAL_LIMIT = 10;

const RewardsSearchV2 = ({ onClose }) => {
  useScrollToTop();
  const [firstLoad, setFirstLoad] = useState(true);
  const [isLoadingRewards, setIsLoadingRewards] = useState(false);
  const [searchedText, setSearchedText] = useState('');
  const [error, setError] = useState('');
  const [searchedBrands, setSearchedBrands] = useState([]);
  const [totalBrands, setTotalBrands] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(INITIAL_LIMIT);
  const [serverExhausted, setServerExhausted] = useState(false);

  const { userData } = useFirebaseAuth();

  const loadSearchedBrands = useCallback(async (
    searchText,
    limit = INITIAL_LIMIT,
  ) => {
    setIsLoadingRewards(true);

    const {
      data,
      error: searchError,
    } = await searchDeals({
      q: searchText,
      limit,
      itemsPerBrand: 5,
      affiliateCid: userData?.affiliate_cid,
      userSegments: userData?.segments?.join(','),
      isMember: userData?.activeSubscription ?? false,
    });

    if (searchError) {
      setSearchedBrands([]);
      setTotalBrands(0);
      setError(searchError?.message || getGenericError());
      setIsLoadingRewards(false);
      return;
    }

    const items = data.items ?? [];
    setSearchedBrands(items);
    setTotalBrands(data.total_brands ?? 0);
    setCurrentLimit(limit);
    setServerExhausted(items.length < limit);
    setError('');
    setIsLoadingRewards(false);
  }, [userData]);

  // Always point to the latest loadSearchedBrands so the debounced function
  // never closes over a stale version (e.g. after userData resolves post-mount)
  const loadSearchedBrandsRef = useRef(loadSearchedBrands);
  useEffect(() => {
    loadSearchedBrandsRef.current = loadSearchedBrands;
  }, [loadSearchedBrands]);

  const debouncedSearch = useRef(
    debounce(async (newTextSearch) => {
      if (newTextSearch.length >= WORD_SEARCH_MIN_LENGTH) {
        await loadSearchedBrandsRef.current(newTextSearch, INITIAL_LIMIT);
        setFirstLoad(false);
      }
    }, SEARCH_DEBOUNCE_TIME),
  );

  const handleLoadMore = useCallback(() => {
    if (isLoadingRewards || serverExhausted) return;
    loadSearchedBrands(searchedText, currentLimit + 10);
  }, [searchedText, currentLimit, loadSearchedBrands, isLoadingRewards, serverExhausted]);

  const resetSearch = useCallback(() => {
    debouncedSearch.current.cancel();
    setSearchedText('');
    setSearchedBrands([]);
    setTotalBrands(0);
    setCurrentLimit(INITIAL_LIMIT);
    setServerExhausted(false);
    setFirstLoad(true);
  }, []);

  const handleOnClose = useCallback(() => {
    resetSearch();
    if (onClose) onClose();
  }, [onClose, resetSearch]);

  const handleChange = useCallback((event) => {
    const newTextSearch = event.target.value;
    if (newTextSearch === '') {
      resetSearch();
    } else {
      setSearchedText(newTextSearch);
      debouncedSearch.current(newTextSearch);
    }
  }, [resetSearch]);

  const hasMore = searchedBrands.length < totalBrands && !serverExhausted;

  return (
    <div className={classNames('RewardsSearchV2', { hasEnvBanner: !isProd() })}>
      <div className="RewardsSearchV2__content">
        <SearchInputButton
          value={searchedText}
          placeholder="Search by brand name"
          onChange={handleChange}
          onClose={handleOnClose}
          onClearSearch={resetSearch}
          isSearching
          autoFocus
        />
        <div className="RewardsSearchV2__content-results">
          <RewardsSearchResultList
            loadMore={handleLoadMore}
            brandsList={searchedBrands}
            isLoadingRewards={isLoadingRewards}
            hasMore={hasMore}
            error={error}
            firstLoad={firstLoad}
            searchedText={searchedText}
          />
        </div>
      </div>
    </div>
  );
};

export default RewardsSearchV2;
